/**
 * "This week" summary card — Mon-Sun rollup of rides, hours, km, TSS.
 *
 * Visible at the top of the Rides page in both list and calendar
 * modes. Lives in its own file because the grouping is reused (the
 * mobile calendar uses the same date logic to compute per-week
 * strips).
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRidesInRange } from '../hooks/useRides.ts';
import type { RideListItem } from '../types/api.ts';

export function WeeklySummary() {
  const { t } = useTranslation();
  const { monday, sunday } = useMemo(() => weekBoundsFor(new Date()), []);

  const { data, isLoading } = useRidesInRange(
    toIsoDate(monday),
    toIsoDate(sunday),
  );
  const totals = useMemo(() => totalsFor(data?.rides ?? []), [data]);

  let body: ReactNode;
  if (isLoading) {
    body = <div className="h-10 rounded-lg bg-(--color-bg-tertiary) animate-pulse" />;
  } else if (totals.rides === 0) {
    body = (
      <p className="text-sm text-(--color-text-secondary)">
        {t('dashboard.weekSummary.empty')}
      </p>
    );
  } else {
    body = (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Stat
          label={t('dashboard.weekSummary.rides')}
          value={`${totals.rides}`}
        />
        <Stat
          label={t('dashboard.weekSummary.hours')}
          value={`${totals.hours.toFixed(1)}h`}
        />
        <Stat
          label={t('dashboard.weekSummary.distance')}
          value={`${totals.km.toFixed(0)} km`}
        />
        <Stat
          label={t('dashboard.weekSummary.tss')}
          value={`${Math.round(totals.tss)}`}
        />
      </div>
    );
  }

  return (
    <section className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
          {t('dashboard.weekSummary.title')}
        </h2>
        <span className="text-xs text-(--color-text-secondary)">
          {formatRange(monday, sunday)}
        </span>
      </div>

      {body}
    </section>
  );
}

function Stat({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2 text-center">
      <div className="text-[10px] text-(--color-text-secondary) uppercase tracking-wide">
        {label}
      </div>
      <div className="text-(--color-text-primary) font-semibold">{value}</div>
    </div>
  );
}

interface WeekTotals {
  rides: number;
  hours: number;
  km: number;
  tss: number;
}

function totalsFor(rides: RideListItem[]): WeekTotals {
  let durationSec = 0;
  let distanceM = 0;
  let tss = 0;
  for (const r of rides) {
    durationSec += r.duration_sec ?? 0;
    distanceM += r.distance_m ?? 0;
    tss += r.training_stress_score ?? 0;
  }
  return {
    rides: rides.length,
    hours: durationSec / 3600,
    km: distanceM / 1000,
    tss,
  };
}

/** Mon-Sun bounds for the ISO week containing ``ref``. */
function weekBoundsFor(ref: Date): { monday: Date; sunday: Date } {
  const monday = new Date(ref);
  // Mon-first: getDay() is 0..6 (Sun..Sat); shift so Mon=0.
  const offset = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - offset);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return { monday, sunday };
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRange(start: Date, end: Date): string {
  const sFmt = start.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const eFmt = end.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return `${sFmt} → ${eFmt}`;
}
